/* RESPONSIBLE TEAM: team-reporting */
/* eslint-disable @intercom/intercom/no-bare-strings */

// Shared constants — re-exported from @intercom/reporting-core
export {
  // Aggregation
  MEDIAN,
  MEAN,
  MAX,
  MIN,
  COUNT,
  CARDINALITY,
  RANGE,
  SORTING_LABEL_NAME,
  SORTING_LABEL_NUMERIC,
  REPORTING_FILTER_SELECT_ALL,
  UNKNOWN,
  PERCENT,
  VALUE,

  // Field identifiers
  CONVERSATION_CREATED_AT,
  CONVERSATION_CREATED_AT_DAY_OF_WEEK,
  COMMENT_CREATED_AT,
  COMMENT_CREATED_AT_DAY_OF_WEEK,
  TEAMMATE_RESPONSE_TIME,
  FIRST_TEAMMATE_RESPONSE_TIME,
  COUNTRY,
  CONTINENT,
  FIRST_USER_CONVERSATION_PART_CREATED_AT,
  FIRST_USER_CONVERSATION_PART_CREATED_AT_DAY_OF_WEEK,
  MAX_FIRST_USER_CONVERSATION_PART_CREATED_AT_DAY_OF_WEEK,
  FIRST_CLOSED_AT,
  FIRST_REOPENED_AT,
  STATE_START,
  CLOSE_DATE,
  MEETING_BOOKED_AT,
  TIME,
  BUCKET,
  HOUR_OF_DAY,
  DAY_OF_WEEK,
  IS_RESOLVED_FIRST_CONTACT,
  CLUSTERED_TEAMMATE_REPLIES_COUNT,
  CONVERSATION_STATE,
  NEW_CONVERSATION_STATE,
  PREVIOUS_CONVERSATION_STATE,
  FIRST_ADMIN_REPLY_AT,
  CONVERSATION_ID,
  REASSIGNMENTS_COUNT_AFTER_ADMIN_REPLY,
  ASSIGNMENT_BEFORE_FIRST_ADMIN_REPLY_TIME,
  LAST_ASSIGNMENT_TO_CLOSE_TIME,
  LAST_TEAM_ASSIGNED_BEFORE_REPLY,
  COMMENT_TEAM_ASSIGNED,
  CHANNEL,
  ES_MISSING_INTEGER_BUCKET_KEY,

  // Grouping
  GEO_GROUPING_LIMIT,
  COUNTRY_GROUPING_TERM_SIZE,
  CONTINENT_GROUPING_TERM_SIZE,
  TEAM_GROUPING_TERM_SIZE,
  TEAM_GROUPING_LIMIT,
  CHANNEL_GROUPING_TERM_SIZE,
  CHANNEL_GROUPING_LIMIT,

  // Mappings
  AVAILABLE_CONTINENT_MAPPINGS,
  AVAILABLE_CHANNEL_MAPPINGS,
  STANDALONE_AVAILABLE_CHANNEL_MAPPINGS,
  AVAILABLE_FIN_ANSWER_TYPE_MAPPINGS,
  AVAILABLE_FIN_RESOLUTION_STATE_MAPPINGS,
  AVAILABLE_SLA_TARGET_TYPE_MAPPINGS,

  // Colors
  COLOR_MAPPINGS,
  SERIES_COLOR_PALETTE,
  CSAT_COLOR_MAPPINGS,
  DEFAULT_HEATMAP_COLORS,
  CSAT_METRICS,

  // Country
  COUNTRY_ALIASES,
  COUNTRY_NAME_TO_VALUE_LOOKUP,

  // Conversation types
  INBOUND,
  INBOUND_TEXT,
  OUTBOUND,
  OUTBOUND_TEXT,
  CONVERSATION_TYPE_MAPPINGS,
  PARTICIPANT_TYPES,
  PARTICIPANT_TYPE_VALUES,

  // Filters
  REPLIES_ONLY_FILTER,
  USER_PART_EXISTS_FILTER,
  EXCLUDE_BOT_PARTS_FILTER,
  CONVERSATION_PART_REPLIES_FILTERS,
  TEAMMATE_REPLIES_FILTERS,
  CLOSES_ONLY_FILTER,
  CLOSES_WITH_USER_PART_FILTERS,
  EXCLUDE_BOT_ADMIN,

  // Property identifiers
  TEAM_PROPERTY_IDENTIFIERS,
  TEAMMATE_PROPERTY_IDENTIFIERS,

  // Conversation ratings
  CONVERSATION_RATING_IDS,

  // Time
  SECONDS_PER_MINUTE,
  SECONDS_PER_HOUR,
  SECONDS_PER_DAY,
  SECONDS_PER_MONTH,
  SECONDS_PER_YEAR,
} from '@intercom/reporting-core';

// Ember-specific constants — these reference Ember component paths and stay here
export const AVAILABLE_FILTER_CONFIGS = {
  continents: {
    selectorLabelIntlKey: 'reporting.flexible-filters.continent.label',
    selectorValue: 'continents',
    component: 'reporting/flexible/filter-bar/continent-filter',
    icon: 'globe',
    isPaywalled: true,
  },
  countries: {
    selectorLabelIntlKey: 'reporting.flexible-filters.countries.label',
    selectorLabel: 'Country',
    selectorValue: 'countries',
    component: 'reporting/flexible/filter-bar/country-filter',
    icon: 'globe',
    isPaywalled: true,
  },
  channels: {
    selectorLabelIntlKey: 'reporting.flexible-filters.channels.label',
    selectorLabel: 'Channel',
    selectorValue: 'channels',
    component: 'reporting/flexible/filter-bar/channel-filter',
    icon: 'multiplatform',
    isPaywalled: true,
  },
  startedByUser: {
    selectorLabelIntlKey: 'reporting.flexible-filters.started-by-user.label',
    selectorLabel: 'Started by',
    selectorValue: 'startedByUser',
    component: 'reporting/flexible/filter-bar/started-by-user-filter',
    icon: 'conversation',
  },
  scopingTagIds: {
    selectorLabelIntlKey: 'reporting.flexible-filters.scoping-tag-ids.label',
    selectorLabel: 'Tag',
    selectorValue: 'scopingTagIds',
    component: 'reporting/flexible/filter-bar/tag-filter',
    icon: 'tag',
  },
  teammatesParticipated: {
    selectorLabelIntlKey: 'reporting.flexible-filters.teammates-participated.label',
    selectorLabel: 'Teammate',
    selectorValue: 'teammatesParticipated',
    component: 'reporting/flexible/filter-bar/teammate-participated-filter',
    icon: 'person',
  },
  teamsParticipated: {
    selectorLabelIntlKey: 'reporting.object-catalog.humanized-names.team-replied-to',
    selectorLabel: 'Team participated',
    selectorValue: 'teamsParticipated',
    component: 'reporting/flexible/filter-bar/team-participated-filter',
    icon: 'multiple-people',
  },
  botInboxTime: {
    selectorLabelIntlKey: 'reporting.flexible-filters.bot-inbox-time.label',
    selectorLabel: 'Bot inbox time',
    selectorValue: 'botInboxTime',
    component: 'reporting/flexible/filter-bar/bot-inbox-time-filter',
    icon: 'conversation',
  },
  teammatesAssigned: {
    selectorLabelIntlKey: 'reporting.object-catalog.humanized-names.teammate-assigned',
    selectorLabel: 'Teammate assigned',
    selectorValue: 'teammatesAssigned',
    component: 'reporting/flexible/filter-bar/teammate-assigned-filter',
    icon: 'person',
  },
  teamsAssigned: {
    selectorLabelIntlKey: 'reporting.object-catalog.humanized-names.team-currently-assigned',
    selectorLabel: 'Team assigned',
    selectorValue: 'teamsAssigned',
    component: 'reporting/flexible/filter-bar/team-assigned-filter',
    icon: 'multiple-people',
  },
  slas: {
    selectorLabelIntlKey: 'reporting.flexible-filters.slas.label',
    selectorLabel: 'SLA',
    selectorValue: 'slas',
    component: 'reporting/flexible/filter-bar/sla-filter',
    icon: 'sla',
  },
  topics: {
    selectorLabelIntlKey: 'reporting.flexible-filters.topics.label',
    selectorLabel: 'Topic',
    selectorValue: 'topics',
    component: 'reporting/flexible/filter-bar/topic-filter',
    icon: 'insights',
  },
  participantTypes: {
    selectorLabelIntlKey: 'reporting.flexible-filters.participant-types.label',
    selectorLabel: 'Participant type',
    selectorValue: 'participantTypes',
    component: 'reporting/flexible/filter-bar/participant-type-filter',
    icon: 'multiple-people',
  },
  ticket_type_ids: {
    selectorLabel: 'Ticket type',
    selectorLabelIntlKey: 'app.lib.reporting.custom.available-filter-configs.ticket-type-id.label',
    selectorValue: 'ticket_type_ids',
    type: 'ticket_type_id',
    component: 'reporting/custom/chart-builder/filter-bar/ticket-type-filter',
    isStandardAttribute: true,
    tooltipText: 'This filters by ticket type.',
    tooltipTextIntlKey: 'app.lib.reporting.custom.available-filter-configs.ticket-type-id.tooltip',
    icon: 'ticket',
    attributeType: 'integer',
    loadKnownValuesSources: ['tickets'],
  },
};

export const AVAILABLE_FILTERS_WITH_CUSTOM_ATTRIBUTES = Object.keys(AVAILABLE_FILTER_CONFIGS)
  .map((key) => AVAILABLE_FILTER_CONFIGS[key].selectorValue)
  .concat('customAttributes', 'teammateId');
